import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { reviews } from '@/mocks/reviews';

const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Galgame资源导航',
  url: 'https://galgame-resource.com/',
  description: '精心汇总主流galgame资源网站，涵盖免费下载、汉化补丁、攻略合集与最新资源更新，帮助玩家告别资源荒。',
  foundingDate: '2024',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const webPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - Galgame资源导航',
  description: '了解Galgame资源导航的品牌故事、用户口碑评价，以及如何联系我们。',
  url: 'https://galgame-resource.com/about',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('idle');
    setFormMessage('');

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d9qocpue3oq4jqumtnu0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(
            Array.from(formData.entries())
              .filter(([key]) => key !== 'website_alt')
              .map(([key, value]) => [key, value.toString()])
          ).toString(),
        }
      );

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      const code = parsed && typeof parsed === 'object' && 'code' in parsed
        ? (parsed as Record<string, unknown>).code
        : null;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed && typeof parsed === 'object' && 'meta' in parsed
          ? String(((parsed as Record<string, unknown>).meta as Record<string, unknown>)?.message || '')
          : (parsed && typeof parsed === 'object' && 'message' in parsed
            ? String((parsed as Record<string, unknown>).message)
            : '');
        setFormStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
              <div className="max-w-3xl">
                <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-4">
                  关于我们
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  Galgame资源导航致力于为中文galgame玩家打造最全面、最可靠的资源导航平台。我们精心筛选并汇总了各类galgame资源网站，涵盖免费下载、汉化补丁、攻略指南与社区论坛，帮助每一位galgame爱好者告别资源荒，轻松畅玩。
                </p>
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-5">
                  我们的故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    作为一名资深galgame玩家，我们深知"找资源"这件事有多令人头疼。日系galgame资源分散在各个角落，有些隐藏在论坛深处，有些随时间流逝而失效。新人玩家更是常常在茫茫网络中迷失方向。
                  </p>
                  <p>
                    正是因为亲身经历过这些困扰，我们决定打造
                    <strong className="text-foreground-900">Galgame资源导航</strong>
                    ——一个专注于galgame资源网站聚合与推荐的平台。我们不提供游戏文件下载，而是帮助玩家找到最可靠的资源来源。
                  </p>
                  <p>
                    自2024年上线以来，我们已经收录了超过
                    <strong className="text-accent-600">12个优质galgame资源网站</strong>
                    ，覆盖下载、汉化、攻略、补丁、社区五大类别。每收录一个站点，我们都会进行严格评估，确保推荐给玩家的都是值得信赖的资源来源。
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-6 mt-8">
                  <div className="text-center">
                    <div className="text-2xl font-heading text-primary-600 mb-1">12+</div>
                    <div className="text-xs text-foreground-500">收录站点</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-heading text-accent-600 mb-1">5</div>
                    <div className="text-xs text-foreground-500">资源分类</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-heading text-primary-600 mb-1">5+</div>
                    <div className="text-xs text-foreground-500">原创文章</div>
                  </div>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=Cozy%20Japanese%20room%20with%20a%20wooden%20desk%2C%20laptop%20displaying%20anime%20style%20visual%20novel%2C%20warm%20afternoon%20sunlight%20through%20window%2C%20bookshelves%20with%20manga%2C%20tea%20cup%20on%20desk%2C%20peaceful%20atmosphere%2C%20soft%20warm%20color%20palette&width=800&height=600&seq=about-story&orientation=landscape"
                  alt="我们的故事 - Galgame资源导航品牌介绍"
                  title="Galgame资源导航品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Reviews */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                  玩家口碑
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  来自galgame社区的真实评价，感谢每一位玩家的支持与反馈
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    className="p-5 rounded-lg bg-background-50 border border-background-200/70"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center font-heading text-sm">
                        {review.avatarInitial}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">
                          {review.username}
                        </div>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`text-[10px] ${
                                i < review.rating
                                  ? 'ri-star-fill text-primary-500'
                                  : 'ri-star-line text-foreground-300'
                              }`}
                            ></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed mb-3">
                      {review.content}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{review.source}</span>
                      <span>{review.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">
                  有任何问题、建议或合作意向，欢迎随时联系我们
                </p>
              </div>

              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5 p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/70"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      昵称
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入你的昵称"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入你的电子邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                    placeholder="请输入你的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {formStatus !== 'idle' && (
                  <div
                    className={`p-4 rounded-md text-sm ${
                      formStatus === 'success'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}
                  >
                    {formMessage}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  发送留言
                </button>
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}